import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { UserClaim } from '@/constants/userClaim';
import { Environment } from '@/constants/environment';

export type ExternalLink = {
  name: string;
  link: string;
};

export type Configuration = {
  internal_host_name: string;
  api_root: string;
  api_root_internal: string;
  reporting_root: string;
  reporting_root_internal: string;
  help_root: string;
  help_root_internal: string;
  notification_api_root: string;
  registration_allowed: boolean;
  external_links: ExternalLink[];
  date_format: string;
  date_locale: string;
  user_claims: UserClaim[];
  user_roles: string[];
  automatic_processing_code_ids_with_disabled_correction_amount: number[];
  automatic_processing_codes_with_disabled_correction_amount_for_budget_json_forms: number[];
  negative_amount_automatic_processing_codes: number[];
  negative_amount_automatic_processing_codes_for_budget_json_forms: number[];
  allowed_negative_amount_report_type_ids: number[];
  allowed_duplicate_form_type_ids: number[];
  automatic_processing_codes_default_page_size: number;
  daysBeforeDueDateAlert: number;
  maxAllowedFilesPerReport: number;
  maxAllowedActiveFilesPerReport: number;
  maxAllowedFileSize: number;
  maxAllowedRowsForDownloads: number;
  maxAllowedSelectorReports: number;
  frontPageHtml: string;
  serviceColor: string;
  maxNumberAttemptsRefreshTokenTabulator: number;
};

export const useConfigStore = defineStore('config', () => {
  const config = ref<Configuration>({
    internal_host_name: '',
    api_root: '',
    api_root_internal: '',
    reporting_root: '',
    reporting_root_internal: '',
    help_root: '',
    help_root_internal: '',
    notification_api_root: '',
    registration_allowed: false,
    external_links: [],
    date_format: '',
    date_locale: '',
    user_claims: [],
    user_roles: [],
    automatic_processing_code_ids_with_disabled_correction_amount: [],
    automatic_processing_codes_with_disabled_correction_amount_for_budget_json_forms: [],
    negative_amount_automatic_processing_codes: [],
    negative_amount_automatic_processing_codes_for_budget_json_forms: [],
    allowed_negative_amount_report_type_ids: [],
    allowed_duplicate_form_type_ids: [],
    automatic_processing_codes_default_page_size: 250,
    daysBeforeDueDateAlert: 0,
    maxAllowedFilesPerReport: 0,
    maxAllowedActiveFilesPerReport: 0,
    maxAllowedFileSize: 0,
    maxAllowedRowsForDownloads: 0,
    maxAllowedSelectorReports: 0,
    frontPageHtml: '',
    serviceColor: '',
    maxNumberAttemptsRefreshTokenTabulator: 0
  });

  const environment = ref<Environment>(import.meta.env.VITE_APP_ENV as Environment);
  const isProductionEnvironment = computed(() => environment.value === Environment.Production);

  function setConfig(newConfig: Configuration): void {
    config.value = newConfig;
  }

  const isInternalHostname = computed(() => window.location.hostname.includes(config.value.internal_host_name));

  const documentationBaseUrl = computed(() =>
    isInternalHostname.value
      ? config.value.help_root_internal
      : config.value.help_root
  );
  const apiRoot = computed(() => (isInternalHostname.value ? config.value.api_root_internal : config.value.api_root));
  const reportingRoot = computed(() =>
    isInternalHostname.value ? config.value.reporting_root_internal : config.value.reporting_root
  );

  async function loadConfig(): Promise<void> {
    const env = import.meta.env.VITE_APP_CONFIG_PATH;
    environment.value = env as Environment;
    if (!environment.value) {
      environment.value = Environment.Production;
    }
    const configJson = await import(`../configuration/${environment.value}.json`);
    config.value = configJson.default;
  }

  return {
    config,
    setConfig,
    loadConfig,
    environment,
    isProductionEnvironment,
    apiRoot,
    reportingRoot,
    documentationBaseUrl
  };
});
